import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { map, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MultiSelect } from 'primeng/multiselect';
import {
  Column,
  CustomTableAction,
  DataTableComponent,
  EmptyTableComponent,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import {
  NimmstaTimeAgoPipe,
  StatusBadgePipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { Organization } from '@nimmsta/ng-commons-lib/models';
import { OrganizationService } from '@nimmsta/ng-commons-lib/services/organization';
import { AdminJob } from '../../models/adminJob';
import { JobAdminService } from '../../services/job-admin.service';
import { JobExportService } from '../../services/job-export.service';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-admin-job',
  templateUrl: './admin-job.component.html',
  imports: [
    MultiSelect,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    DataTableComponent,
    TranslatePipe,
    EmptyTableComponent,
    ErrorComponent,
    HeaderBarComponent,
  ],
  providers: [NimmstaTimeAgoPipe, StatusBadgePipe],
})
export class AdminJobComponent implements OnInit, AfterViewInit, OnDestroy {
  private jobService = inject(JobAdminService);
  private orgService = inject(OrganizationService);
  private router = inject(Router);
  private timeago = inject(NimmstaTimeAgoPipe);
  private statusBadge = inject(StatusBadgePipe);
  private jobExportService = inject(JobExportService);

  jobSource!: NimmstaDataSource<AdminJob>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<AdminJob>;

  @ViewChild(MultiSelect) ms!: MultiSelect;

  get columns(): Column[] {
    return [
      new Column('name', 'job.name', { sortable: true }),
      new Column('status', 'job.status', {
        pipe: this.statusBadge,
        sortable: true,
      }),
      new Column('lastUpdatedAt', 'job.lastUpdatedAt', {
        pipe: this.timeago,
        sortable: true,
        invertSorting: true,
      }),
      new Column('organization.name', 'organization', { sortable: true }),
    ];
  }

  customTableActions: CustomTableAction<AdminJob>[] = [];

  error?: HttpErrorResponse;

  exportAction?: CustomTableAction<AdminJob>;

  reloadInterval?: ReturnType<typeof setInterval>;

  private exportSubscription?: Subscription;

  orgs = toSignal(
    this.orgService
      .list({
        sortColumn: 'name',
        pageSize: 9999,
      })
      .pipe(map((organizations) => organizations.content)),
    { initialValue: [] as Organization[] },
  );

  selected: Organization[] = [];

  selectFilterError = false;

  ngOnInit(): void {
    this.jobSource = new NimmstaDataSource<AdminJob>(this.jobService);
    this.jobSource.defaultFilter = [
      'status:IN_PROGRESS-or',
      'status:COMPLETE-or',
      'status:CANCELLED-or',
      'status:DELETED-or',
    ];
    this.exportAction = new CustomTableAction('job.admin.export', 'download');
    this.customTableActions.push(this.exportAction);
    this.exportAction.subscribe(() => this.export());
    // Reload table every 10 seconds
    this.reloadInterval = setInterval(
      () => this.tableComponent.loadDataPage(),
      10000,
    );
  }

  ngAfterViewInit(): void {
    // Fix bug in PrimeNG MultiSelect where chip removal does not trigger change event
    const originalRemoveChip = this.ms.removeOption;
    this.ms.removeOption = (...args) => {
      originalRemoveChip.apply(this.ms, args);
      this.loadFiltered();
    };
  }

  ngOnDestroy(): void {
    clearInterval(this.reloadInterval);
    this.exportSubscription?.unsubscribe();
  }

  show(id: string) {
    this.router.navigate(['/admin-jobs', id]);
  }

  export() {
    if (this.exportAction?.loading) {
      return;
    }

    this.selectFilterError = false;
    if (this.selected.length === 0) {
      this.selectFilterError = true;
      return;
    }
    this.exportAction!.loading = true;
    this.error = undefined;
    this.exportSubscription?.unsubscribe();
    this.exportSubscription = this.jobExportService
      .startAdminExport({
        organizations: this.selected.map((organization) => organization.id),
        mode: 'AUTO',
      })
      .subscribe({
        next: (response) => {
          if (response.status !== 'COMPLETED' || !response.downloadUrl) {
            this.error = this.createExportFailedError(response.errorMessage);
            this.exportAction!.loading = false;
            return;
          }

          this.jobExportService.downloadExportFile(response.downloadUrl);
          this.exportAction!.loading = false;
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            const customError = { ...error };
            customError.error.error = 'error.custom.no-jobs-to-export';
            this.error = customError;
          } else {
            this.error = error;
          }
          this.exportAction!.loading = false;
        },
      });
  }

  loadFiltered() {
    this.jobService.selectedOrgs = this.selected;
    this.tableComponent.loadDataPage();
  }

  private createExportFailedError(
    errorMessage: string | null,
  ): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 500,
      statusText: 'Export failed',
      error: {
        error: errorMessage || 'error.InternalServerError',
      },
    });
  }
}
